/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    DoCheck,
    ElementRef,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    SimpleChanges
} from '@angular/core';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { ReportDisplayService } from '@reports/services/report-display.service';
import { ReportService } from '@reports/services/report.service';
import { ReportState } from '@reports/state/report.state';
import { DashGisUtil } from '@shared/dash-gis-util';
import { LoggerService } from '@shared/services/logger.service';
import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { IsolatedReportService } from '../services/isolatedReport.service';
import { ReloadActiveReports, SetFilterOptions } from '../state/report.actions';
import { ReportDefinition, ReportLaunchOptions } from '../state/report.model';

export function isolatedReportFactory(injector: Injector, irs: IsolatedReportService) {
    return (irs || new IsolatedReportService(injector.get(LoggerService)));
}

@Directive()
export abstract class TypedReportDirective
    implements OnInit, OnChanges, OnDestroy, DoCheck, AfterContentChecked, AfterViewInit {
    clearReports = output<void>();
    recordCount = output<number>();
    reportTitle = output<string>();
    @Input() launchOptions: ReportLaunchOptions;
    @Input() availableReports: ReportDefinition[];

    @Input() showControls: boolean;
    @Input() uid: string;

    abstract reportData: any[];

    readonly featureReportKeys$: Observable<any[]>;

    protected disposeSubject = new Subject<void>();
    protected dispose$: Observable<void>;
    protected recordCountInternal = -1;

    private lastClientRect: DOMRect;

    constructor(
        protected logger: LoggerService,
        protected reportService: ReportService,
        protected isolatedReport: IsolatedReportService,
        protected cdr: ChangeDetectorRef,
        protected actions$: Actions,
        protected store: Store,
        protected el: ElementRef
    ) {
        this.dispose$ = this.disposeSubject.asObservable();
        this.actions$
            .pipe(
                ofActionSuccessful(ReloadActiveReports),
                takeUntil(this.dispose$)
            )
            .subscribe(() => this.isolatedReport.triggerReport());
        this.featureReportKeys$ = this.store.select(ReportState.featureReportKeys).pipe(
            map((fn) => fn(this.definition.layer))
        );
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.uid) {
            this.isolatedReport.id = changes.uid.currentValue;
        }
        const optsChange = changes.launchOptions;
        if (optsChange?.currentValue && !isEqual(optsChange.currentValue, optsChange.previousValue)) {
            const filters = this.launchOptions.filters;
            if (filters && filters.length > 0) {
                this.store.dispatch(
                    new SetFilterOptions({
                        id: this.id,
                        reportName: this.definition.name,
                        filterOptions: filters
                    })
                );
            }
        }
    }

    ngOnInit() {
        this.isolatedReport.markChanged$.pipe(takeUntil(this.dispose$)).subscribe(() => this.cdr.detectChanges());
        this.watchElementSize();
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    ngDoCheck(): void {
        const dims = this.el.nativeElement.getBoundingClientRect();
        if (!DashGisUtil.compareRect(dims, this.lastClientRect)) {
            this.isolatedReport.setContainerDimension([ dims.width, dims.height ]);
            this.lastClientRect = dims;
        }
    }

    ngAfterContentChecked() {
        if (this.definition) {
            this.reportTitle.emit(this.definition.displayName);
        }
    }

    ngAfterViewInit(): void {
        // necessary to satisfy interface
    }

    setRecordCount(count: number) {
        this.recordCountInternal = count;
        this.recordCount.emit(count);
    }

    get definition(): ReportDefinition {
        return this.launchOptions.definition;
    }

    get id() {
        return this.isolatedReport.id;
    }

    private async watchElementSize() {
        const rect = this.el.nativeElement.getBoundingClientRect();
        this.isolatedReport.setContainerDimension([ rect.width, rect.height ]);
    }
}
